import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

type Outcome = 'created' | 'matched_existing' | 'skipped';
type MatchedBy = 'primary_email' | 'primary_phone' | null;

interface Diagnosis {
  rowNumber: number;
  display_name: string | null;
  outcome: Outcome;
  matched_by: MatchedBy;
  existing_contact_id: number | null;
  warnings: string[];
}

interface ImportSummary {
  total: number;
  created: number;
  matched_existing: number;
  skipped: number;
}

interface ImportResponse {
  dry_run: boolean;
  parse_warnings: string[];
  diagnoses: Diagnosis[];
  summary: ImportSummary;
}

type Step =
  | { kind: 'upload' }
  | { kind: 'previewing' }
  | { kind: 'preview'; response: ImportResponse }
  | { kind: 'committing'; response: ImportResponse }
  | { kind: 'result'; response: ImportResponse };

const OUTCOME_CHIP: Record<Outcome, string> = {
  created: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
  matched_existing: 'bg-sky-50 text-sky-900 ring-sky-200',
  skipped: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const OUTCOME_LABEL: Record<Outcome, string> = {
  created: 'Created',
  matched_existing: 'Matched existing',
  skipped: 'Skipped',
};

const RECOGNIZED_HEADERS: Array<{ canonical: string; aliases: string[] }> = [
  { canonical: 'display_name', aliases: ['Name', 'Display Name', 'Full Name', 'Contact'] },
  { canonical: 'primary_email', aliases: ['Email', 'Primary Email'] },
  { canonical: 'primary_phone', aliases: ['Phone', 'Primary Phone', 'Mobile'] },
  { canonical: 'company', aliases: ['Company', 'Organization'] },
  { canonical: 'lifecycle_stage', aliases: ['Lifecycle', 'Stage'] },
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function countLines(text: string): number {
  if (!text) return 0;
  let lines = 0;
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) === 10) lines++;
  }
  // count last line if no trailing newline
  if (text.length > 0 && text.charCodeAt(text.length - 1) !== 10) lines++;
  return lines;
}

async function postImport(csvText: string, dryRun: boolean): Promise<ImportResponse> {
  const res = await fetch('/api/imports/contacts', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ csv_text: csvText, dry_run: dryRun }),
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      detail = body.error ?? body.message ?? '';
    } catch {
      // ignore
    }
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return (await res.json()) as ImportResponse;
}

export function CSVImportFlow(): JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number>(0);
  const [csvText, setCsvText] = useState<string>('');
  const [step, setStep] = useState<Step>({ kind: 'upload' });
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const lineCount = useMemo(() => countLines(csvText), [csvText]);
  const hasFile = csvText.length > 0;
  const isEmptyFile = fileName !== null && csvText.trim().length === 0;

  const resetAll = useCallback(() => {
    setFileName(null);
    setFileSize(0);
    setCsvText('');
    setStep({ kind: 'upload' });
    setError(null);
    setIsDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFile = useCallback((file: File) => {
    setError(null);
    setFileName(file.name);
    setFileSize(file.size);
    const reader = new FileReader();
    reader.onerror = () => {
      setError('Failed to read file.');
      setCsvText('');
    };
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setCsvText(text);
    };
    reader.readAsText(file);
  }, []);

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const runPreview = async () => {
    if (!csvText) return;
    setError(null);
    setStep({ kind: 'previewing' });
    try {
      const response = await postImport(csvText, true);
      setStep({ kind: 'preview', response });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep({ kind: 'upload' });
    }
  };

  const runCommit = async () => {
    if (!csvText || step.kind !== 'preview') return;
    setError(null);
    setStep({ kind: 'committing', response: step.response });
    try {
      const response = await postImport(csvText, false);
      setStep({ kind: 'result', response });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // Stay on preview so user can retry
      setStep({ kind: 'preview', response: step.response });
    }
  };

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-slate-50 px-4 py-5 text-slate-900 sm:px-6">
      <div className="mx-auto w-full max-w-7xl">
        <header className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              System tools
            </p>
            <h1 className="text-2xl font-semibold text-slate-950">CSV contact import</h1>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Use this when a structured contact source has no automated importer. Every file
              runs through a dry-run diagnosis before it can write contacts.
            </p>
          </div>
          <Link
            to="/contacts"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-center text-sm font-medium text-slate-700 hover:bg-slate-100 sm:w-auto"
          >
            Open contacts
          </Link>
        </header>

        <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">File</div>
            <div className="mt-1 truncate text-sm font-semibold text-slate-950">
              {fileName ?? 'Not selected'}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Rows detected</div>
            <div className="mt-1 text-2xl font-semibold text-slate-950">
              {lineCount > 0 ? lineCount : '—'}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Current stage</div>
            <div className="mt-2 text-sm font-semibold text-slate-950">
              {step.kind === 'upload' || step.kind === 'previewing'
                ? 'File intake'
                : step.kind === 'preview' || step.kind === 'committing'
                ? 'Dry-run diagnosis'
                : 'Import result'}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
            <div className="text-xs font-medium text-slate-500">Write guard</div>
            <div className="mt-2 text-sm font-semibold text-slate-950">
              {step.kind === 'result' ? 'Committed' : 'Dry-run first'}
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        {(step.kind === 'upload' || step.kind === 'previewing') && (
          <UploadStep
            fileName={fileName}
            fileSize={fileSize}
            lineCount={lineCount}
            hasFile={hasFile}
            isEmptyFile={isEmptyFile}
            isDragOver={isDragOver}
            isLoading={step.kind === 'previewing'}
            fileInputRef={fileInputRef}
            onFileInputChange={onFileInputChange}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onPreview={runPreview}
            onReset={resetAll}
          />
        )}

        {step.kind === 'preview' && (
          <PreviewStep
            response={step.response}
            onCommit={runCommit}
            onReset={resetAll}
            isCommitting={false}
          />
        )}

        {step.kind === 'committing' && (
          <PreviewStep
            response={step.response}
            onCommit={runCommit}
            onReset={resetAll}
            isCommitting={true}
          />
        )}

        {step.kind === 'result' && (
          <ResultStep response={step.response} onReset={resetAll} />
        )}
      </div>
    </div>
  );
}

interface UploadStepProps {
  fileName: string | null;
  fileSize: number;
  lineCount: number;
  hasFile: boolean;
  isEmptyFile: boolean;
  isDragOver: boolean;
  isLoading: boolean;
  fileInputRef: React.MutableRefObject<HTMLInputElement | null>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onPreview: () => void;
  onReset: () => void;
}

function UploadStep(props: UploadStepProps): JSX.Element {
  const {
    fileName,
    fileSize,
    lineCount,
    hasFile,
    isEmptyFile,
    isDragOver,
    isLoading,
    fileInputRef,
    onFileInputChange,
    onDrop,
    onDragOver,
    onDragLeave,
    onPreview,
    onReset,
  } = props;

  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <section className="min-w-0 rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="mb-4 border-b border-slate-100 pb-4">
          <h2 className="text-sm font-semibold text-slate-950">File intake</h2>
          <p className="mt-1 text-xs text-slate-500">
            Upload the CSV and run a dry-run diagnosis. Nothing is written until the
            preview is committed.
          </p>
        </div>
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={[
            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition',
            isDragOver
              ? 'border-slate-900 bg-slate-50 ring-2 ring-slate-900'
              : 'border-slate-300 bg-white hover:border-slate-900',
          ].join(' ')}
        >
          <p className="text-sm font-medium text-slate-900">
            Drop a CSV here, or
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="ml-1 underline underline-offset-2 hover:text-slate-700"
            >
              choose a file
            </button>
            .
          </p>
          <p className="mt-1 text-xs text-slate-500">Max 2 MB. UTF-8 text recommended.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFileInputChange}
          />
        </div>

        {fileName && (
          <div className="mt-4 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-800 ring-1 ring-slate-200">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-medium">{fileName}</span>
              <span className="text-slate-500">{formatBytes(fileSize)}</span>
              <span className="text-slate-500">
                {lineCount} {lineCount === 1 ? 'line' : 'lines'}
              </span>
            </div>
            {isEmptyFile && (
              <p className="mt-2 text-amber-800">No rows to import — file is empty.</p>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onPreview}
            disabled={!hasFile || isEmptyFile || isLoading}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {isLoading ? 'Previewing…' : 'Preview (dry-run)'}
          </button>
          {fileName && (
            <button
              type="button"
              onClick={onReset}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Clear
            </button>
          )}
        </div>
      </section>

      <aside className="min-w-0 space-y-4">
      <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-950">Import path</h2>
        <ol className="mt-3 space-y-3 text-sm text-slate-600">
          <li>
            <span className="font-medium text-slate-900">1. Upload file</span>
            <br />
            Browser reads the CSV text locally.
          </li>
          <li>
            <span className="font-medium text-slate-900">2. Preview diagnosis</span>
            <br />
            Server reports created, matched, skipped, and warnings.
          </li>
          <li>
            <span className="font-medium text-slate-900">3. Commit import</span>
            <br />
            Contacts are created or matched by email and last-10 phone.
          </li>
        </ol>
      </section>

      <section className="rounded-lg bg-white p-4 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-900">Recognized headers</h2>
        <p className="mt-1 text-xs text-slate-500">
          Header names are case-insensitive. Unrecognized columns are stored under
          <code className="mx-1 rounded bg-slate-100 px-1 py-0.5 text-[11px] text-slate-700">
            contact_properties
          </code>
          as JSON.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          {RECOGNIZED_HEADERS.map((h) => (
            <li key={h.canonical} className="flex flex-wrap items-baseline gap-x-2">
              <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-800">
                {h.canonical}
              </code>
              <span className="text-slate-500">←</span>
              <span className="text-slate-700">{h.aliases.join(' / ')}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600 ring-1 ring-slate-200">
          <p className="font-medium text-slate-800">Example</p>
          <pre className="mt-1 whitespace-pre overflow-x-auto text-[11px] leading-snug">
{`Name,Email,Phone,Company,Lifecycle
Jane Doe,jane@example.com,720-555-0101,Acme Vans,lead
John Smith,john@example.com,(303) 555-0199,,customer`}
          </pre>
          <p className="mt-2 text-slate-500">
            Lifecycle must be one of: lead / qualified / customer / past_customer / dead.
            Invalid values are dropped with a warning.
          </p>
        </div>
      </section>
      </aside>
    </div>
  );
}

interface PreviewStepProps {
  response: ImportResponse;
  onCommit: () => void;
  onReset: () => void;
  isCommitting: boolean;
}

function PreviewStep(props: PreviewStepProps): JSX.Element {
  const { response, onCommit, onReset, isCommitting } = props;
  return (
    <div className="space-y-6">
      <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Dry-run diagnosis
            </p>
            <h2 className="mt-1 text-lg font-semibold tracking-tight">Review import impact</h2>
            <p className="mt-1 text-sm text-slate-600">
              This preview has not written contacts yet. Commit only if the matched and skipped
              rows look correct.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onReset}
              disabled={isCommitting}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Start over
            </button>
            <button
              type="button"
              onClick={onCommit}
              disabled={isCommitting}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {isCommitting ? 'Importing...' : 'Commit import'}
            </button>
          </div>
        </div>

        <SummaryChips summary={response.summary} className="mt-4" />

        {response.parse_warnings.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Parse warnings</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {response.parse_warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <DiagnosesTable diagnoses={response.diagnoses} />
    </div>
  );
}

interface ResultStepProps {
  response: ImportResponse;
  onReset: () => void;
}

function ResultStep(props: ResultStepProps): JSX.Element {
  const { response, onReset } = props;
  const createdIds = response.diagnoses
    .filter((d) => d.outcome === 'created' && d.existing_contact_id !== null)
    .map((d) => d.existing_contact_id as number);

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
              Import committed
            </p>
            <h2 className="mt-1 text-lg font-semibold text-emerald-950">
              Imported {response.summary.created}{' '}
              {response.summary.created === 1 ? 'contact' : 'contacts'}
            </h2>
            <p className="mt-1 text-sm text-emerald-900/80">
              {response.summary.matched_existing} matched existing · {response.summary.skipped} skipped
              · {response.summary.total} rows total
            </p>
          </div>
          <button
            type="button"
            onClick={onReset}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800"
          >
            Import another
          </button>
        </div>
      </section>

      {createdIds.length > 0 && (
        <section className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Newly created contacts</h3>
          <ul className="mt-3 flex flex-wrap gap-2 text-sm">
            {createdIds.map((id) => (
              <li key={id}>
                <Link
                  to={`/contacts/${id}`}
                  className="rounded-md bg-slate-100 px-2 py-1 text-slate-800 ring-1 ring-slate-200 transition hover:bg-slate-200"
                >
                  #{id}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {response.parse_warnings.length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-amber-900">Parse warnings</h3>
          <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-amber-900">
            {response.parse_warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </section>
      )}

      <DiagnosesTable diagnoses={response.diagnoses} />
    </div>
  );
}

interface SummaryChipsProps {
  summary: ImportSummary;
  className?: string;
}

function SummaryChips({ summary, className }: SummaryChipsProps): JSX.Element {
  const chips: Array<{ label: string; value: number; cls: string }> = [
    { label: 'Total', value: summary.total, cls: 'bg-slate-100 text-slate-800 ring-slate-200' },
    {
      label: 'Created',
      value: summary.created,
      cls: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
    },
    {
      label: 'Matched existing',
      value: summary.matched_existing,
      cls: 'bg-sky-50 text-sky-900 ring-sky-200',
    },
    {
      label: 'Skipped',
      value: summary.skipped,
      cls: 'bg-slate-100 text-slate-700 ring-slate-200',
    },
  ];
  return (
    <div className={['flex flex-wrap gap-2 text-xs', className ?? ''].join(' ')}>
      {chips.map((c) => (
        <span
          key={c.label}
          className={['rounded-full px-2.5 py-1 font-medium ring-1', c.cls].join(' ')}
        >
          {c.label}: {c.value}
        </span>
      ))}
    </div>
  );
}

interface DiagnosesTableProps {
  diagnoses: Diagnosis[];
}

function DiagnosesTable({ diagnoses }: DiagnosesTableProps): JSX.Element {
  if (diagnoses.length === 0) {
    return (
      <section className="rounded-lg bg-white p-6 text-sm text-slate-600 shadow-sm ring-1 ring-slate-200">
        No rows to display.
      </section>
    );
  }
  return (
    <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-slate-200">
      <div className="border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-950">Row diagnosis</h2>
        <p className="mt-1 text-xs text-slate-500">
          Review warnings before committing; skipped rows will not create contacts.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[820px] w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Row</th>
              <th className="px-4 py-2">Display name</th>
              <th className="px-4 py-2">Outcome</th>
              <th className="px-4 py-2">Matched by</th>
              <th className="px-4 py-2">Existing ID</th>
              <th className="px-4 py-2">Warnings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {diagnoses.map((d) => (
              <tr key={d.rowNumber} className="align-top">
                <td className="px-4 py-2 text-slate-500">{d.rowNumber}</td>
                <td className="px-4 py-2 text-slate-900">
                  {d.display_name ?? <span className="italic text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-xs font-medium ring-1',
                      OUTCOME_CHIP[d.outcome],
                    ].join(' ')}
                  >
                    {OUTCOME_LABEL[d.outcome]}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-700">
                  {d.matched_by ?? <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  {d.existing_contact_id !== null ? (
                    <Link
                      to={`/contacts/${d.existing_contact_id}`}
                      className="text-slate-900 underline underline-offset-2 hover:text-slate-700"
                    >
                      #{d.existing_contact_id}
                    </Link>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-slate-600">
                  {d.warnings.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <ul className="list-disc space-y-0.5 pl-4">
                      {d.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
